import { DrawDashboardTable } from './DrawDashboardTable';
import { DrawStatusCounts } from './DrawStatusFilter';
import { useMemo, useState } from 'react';
import { DrawStatus } from '@apply/constants';
import { CustomStore } from '@apply/ui';
import { SearchResponse } from '@apply/api/src/api/bfiApi';
import { applicationInsights } from '../../ApplicationInsights';
import Query from 'devextreme/data/query';
import { DrawStatusFilter } from './DrawStatusFilter';
import { DrawApi, IDrawDashboardGrid } from '@apply/api/src/api/DrawApi';
import { SectionHeader, DateFilter } from '../../common/SharedComponents';
import dayjs from 'dayjs';

export const DrawDashboard: React.FC = () => {
    const today = dayjs();
    const [startDate, set_startDate] = useState<Date>(today.subtract(30, 'day').toDate());
    const [endDate, set_endDate] = useState<Date>(today.toDate());
    const [filterDrawStatus, set_filterDrawStatus] = useState<DrawStatus | undefined>(undefined);

    const [statusCounts, set_statusCounts] = useState<DrawStatusCounts>({
        all: 0,
        inProgress: 0,
        request: 0,
        inspecting: 0,
        needToFund: 0,
        notFinalized: 0,
        finalized: 0,
    });

    const drawApi = new DrawApi(applicationInsights);

    const customStore = useMemo(
        () =>
            new CustomStore({
                key: 'bfiNumber',
                load: async (options) => {
                    const response: SearchResponse<IDrawDashboardGrid[]> = await drawApi.dashboard({
                        startDate,
                        endDate,
                        drawStatus: filterDrawStatus,
                    });

                    if (options.filter) {
                        response.result.data = Query(response.result.data)
                            .filter(options.filter)
                            .select(
                                'drawBudgetId',
                                'entityName',
                                'bfiNumber',
                                'loanNumber',
                                'loanType',
                                'drawType',
                                'drawStatus',
                                'borrowerRequest',
                                'pointOfContact',
                                'submitted',
                                'fundedOn',
                                'inspectionOrdered',
                                'inspectedOn'
                            )
                            .toArray();
                    }

                    if (filterDrawStatus === undefined) calculateStatusTotals(response.result.data);

                    return response.result;
                },
            }),
        [startDate, endDate, filterDrawStatus]
    );

    function calculateStatusTotals(data: IDrawDashboardGrid[]) {
        const counts: DrawStatusCounts = {
            all: 0,
            inProgress: 0,
            request: 0,
            inspecting: 0,
            needToFund: 0,
            notFinalized: 0,
            finalized: 0,
        };

        data.forEach((d) => {
            switch (d.drawStatus) {
                case DrawStatus.InProgress:
                    counts.inProgress++;
                    break;
                case DrawStatus.Request:
                    counts.request++;
                    break;
                case DrawStatus.Inspecting:
                    counts.inspecting++;
                    break;
                case DrawStatus.NeedToFund:
                    counts.needToFund++;
                    break;
                case DrawStatus.NotFinalized:
                    counts.notFinalized++;
                    break;
                case DrawStatus.Finalized:
                    counts.finalized++;
                    break;
            }

            counts.all =
                counts.inProgress +
                counts.request +
                counts.inspecting +
                counts.needToFund +
                counts.notFinalized +
                counts.finalized;
        });

        set_statusCounts(counts);
    }

    return (
        <>
            <SectionHeader title='Manage Draws' />
            <div
                className='main-content'
                style={{ padding: 0, backgroundColor: '#f4f4f4', height: '100%' }}
            >
                <div
                    className='scrollable-content'
                    style={{ height: '100%' }}
                >
                    <DrawStatusFilter
                        filterDrawStatus={filterDrawStatus}
                        set_drawStatus={set_filterDrawStatus}
                        {...statusCounts}
                    />
                    <DateFilter
                        startDate={startDate}
                        set_startDate={set_startDate}
                        endDate={endDate}
                        set_endDate={set_endDate}
                    />
                    <DrawDashboardTable customStore={customStore} />
                </div>
            </div>
        </>
    );
};
